/*
 * ============================================================
 * BANDITOS MOTION ENGINE v0.3
 *
 * Supports:
 *
 * 1. Element parallax
 *    Attribute:
 *    data-banditos-parallax
 *
 *    Optional settings:
 *    data-parallax-speed="0.08"
 *    data-parallax-limit="24"
 *
 * 2. Image-banner background motion
 *    Attribute:
 *    data-banditos-background-motion
 *
 *    Settings:
 *    data-motion-type="parallax"
 *    data-motion-speed="0.03"
 *    data-motion-limit="12"
 *
 * Desktop only: 1025px and wider.
 * Respects prefers-reduced-motion.
 * ============================================================
 */

(() => {
    const ELEMENT_SELECTOR = '[data-banditos-parallax]';
    const BACKGROUND_SELECTOR =
        '[data-banditos-background-motion]';

    const DESKTOP_QUERY = window.matchMedia(
        '(min-width: 1025px)'
    );

    const REDUCED_MOTION_QUERY = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
    );

    let elementItems = [];
    let backgroundItems = [];
    let ticking = false;

    /**
     * Prevent a calculated movement from exceeding its limit.
     */
    const clamp = (value, min, max) => {
        return Math.min(Math.max(value, min), max);
    };

    /**
     * Safely convert a data attribute into a number.
     */
    const getNumber = (value, fallback) => {
        const parsedValue = Number.parseFloat(value);

        return Number.isFinite(parsedValue)
            ? parsedValue
            : fallback;
    };

    /**
     * Collect all regular parallax elements.
     */
    const collectElementItems = () => {
        elementItems = Array.from(
            document.querySelectorAll(ELEMENT_SELECTOR)
        ).map((element) => {
            const speed = getNumber(
                element.dataset.parallaxSpeed,
                0.08
            );

            const limit = getNumber(
                element.dataset.parallaxLimit,
                24
            );

            element.style.willChange = 'transform';

            return {
                element,
                speed,
                limit
            };
        });
    };

    /**
     * Collect all image-banner background motion sections.
     */
    const collectBackgroundItems = () => {
        backgroundItems = Array.from(
            document.querySelectorAll(BACKGROUND_SELECTOR)
        ).map((element) => {
            const motionType =
                element.dataset.motionType || 'parallax';

            const speed = getNumber(
                element.dataset.motionSpeed,
                0.03
            );

            const limit = getNumber(
                element.dataset.motionLimit,
                12
            );

            return {
                element,
                motionType,
                speed,
                limit
            };
        });
    };

    /**
     * Refresh all elements known by the engine.
     */
    const collectItems = () => {
        collectElementItems();
        collectBackgroundItems();
    };

    /**
     * Remove motion values when the effect is unavailable.
     */
    const resetItems = () => {
        elementItems.forEach(({ element }) => {
            element.style.removeProperty(
                '--banditos-parallax-y'
            );

            element.classList.remove(
                'banditos-parallax--active'
            );
        });

        backgroundItems.forEach(({ element }) => {
            element.style.removeProperty(
                '--banditos-background-y'
            );

            element.classList.remove(
                'banditos-background-motion--active'
            );
        });
    };

    /**
     * Calculate movement relative to the viewport centre.
     */
    const calculateMovement = (
        element,
        speed,
        limit,
        viewportCenter
    ) => {
        const rect = element.getBoundingClientRect();

        /*
         * Skip elements that are well outside the viewport.
         */
        if (
            rect.bottom < -limit * 4 ||
            rect.top > window.innerHeight + limit * 4
        ) {
            return null;
        }

        const elementCenter =
            rect.top + rect.height / 2;

        const distanceFromCenter =
            elementCenter - viewportCenter;

        return clamp(
            distanceFromCenter * speed * -1,
            -limit,
            limit
        );
    };

    /**
     * Update regular element parallax.
     */
    const updateElementParallax = (viewportCenter) => {
        elementItems.forEach(
            ({ element, speed, limit }) => {
                const movement = calculateMovement(
                    element,
                    speed,
                    limit,
                    viewportCenter
                );

                if (movement === null) {
                    return;
                }

                element.style.setProperty(
                    '--banditos-parallax-y',
                    `${movement.toFixed(2)}px`
                );

                element.classList.add(
                    'banditos-parallax--active'
                );
            }
        );
    };

    /**
     * Update the independent image-banner background layer.
     */
    const updateBackgroundMotion = (viewportCenter) => {
        backgroundItems.forEach(
            ({
                element,
                motionType,
                speed,
                limit
            }) => {
                /*
                 * Version 0.3 currently activates only
                 * the scroll-parallax background effect.
                 *
                 * Zoom, floating and pulse will be added
                 * individually after parallax is tested.
                 */
                if (motionType !== 'parallax') {
                    element.style.setProperty(
                        '--banditos-background-y',
                        '0px'
                    );

                    element.classList.remove(
                        'banditos-background-motion--active'
                    );

                    return;
                }

                const movement = calculateMovement(
                    element,
                    speed,
                    limit,
                    viewportCenter
                );

                if (movement === null) {
                    return;
                }

                element.style.setProperty(
                    '--banditos-background-y',
                    `${movement.toFixed(2)}px`
                );

                element.classList.add(
                    'banditos-background-motion--active'
                );
            }
        );
    };

    /**
     * Update all supported motion effects.
     */
    const updateMotion = () => {
        ticking = false;

        if (
            !DESKTOP_QUERY.matches ||
            REDUCED_MOTION_QUERY.matches
        ) {
            resetItems();
            return;
        }

        const viewportCenter =
            window.innerHeight / 2;

        updateElementParallax(viewportCenter);
        updateBackgroundMotion(viewportCenter);
    };

    /**
     * Prevent repeated calculations during one frame.
     */
    const requestUpdate = () => {
        if (ticking) {
            return;
        }

        ticking = true;

        window.requestAnimationFrame(updateMotion);
    };

    /**
     * Recollect the page and calculate the initial position.
     */
    const initialize = () => {
        collectItems();
        requestUpdate();
    };

    window.addEventListener(
        'scroll',
        requestUpdate,
        { passive: true }
    );

    window.addEventListener(
        'resize',
        () => {
            collectItems();
            requestUpdate();
        }
    );

    /*
     * Reinitialize when Shopify reloads or reorders a section
     * inside the Theme Editor.
     */
    document.addEventListener(
        'shopify:section:load',
        initialize
    );

    document.addEventListener(
        'shopify:section:reorder',
        initialize
    );

    /*
     * Recalculate when a Theme Editor setting changes.
     */
    document.addEventListener(
        'shopify:section:select',
        requestUpdate
    );

    if (document.readyState === 'loading') {
        document.addEventListener(
            'DOMContentLoaded',
            initialize,
            { once: true }
        );
    } else {
        initialize();
    }
})();